import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Button from '#components/controls/button/Button';
import useTranslation from 'next-translate/useTranslation';
import Close from '#components/icons/Close';
import IconButton from '#components/controls/icon-button/IconButton';
import PageTitle from '#components/common/page-title/PageTitle';
import { getTranslatedMediaDuration } from '#utils/getMediaDuration';
import TextLink from '#components/common/textLink/TextLink';
import Typography from '#components/typography/Typography';
import Mediabox from '#components/common/mediabox/Mediabox';
import CircularProgress from '#components/feedback/circular-progress/CircularProgress';

export enum CheckoutContext {
  general = 'general',
  nextEpisode = 'nextEpisode',
}

const translationKeys = {
  general: {
    title: 'checkoutModalTitle',
    description: 'checkoutModalDescription',
  },
  nextEpisode: {
    title: 'nextEpisodeCheckoutModalTitle',
    description: 'nextEpisodeCheckoutModalDescription',
  },
};

type Props = {
  open: boolean;
  loading?: boolean;
  title: string;
  context: CheckoutContext;
  mediaDuration: number;
  mediaQuality: string;
  productLifetime: number;
  productPrice: number;
  onCancel: () => void;
  onConfirm: () => void;
  error?: string;
};

function getLifetimeInfo(i18n, lifetime): string {
  if (lifetime <= 3) {
    const hours = lifetime * 24;
    return `${hours} ${i18n('hours', { count: hours })}`;
  } else {
    return `${lifetime} ${i18n('days', { count: lifetime })}`;
  }
}

function DialogCheckout({
  error,
  loading,
  mediaDuration,
  mediaQuality,
  onCancel,
  onConfirm,
  open,
  productLifetime,
  productPrice,
  title,
  context,
}: Props) {
  const { t, lang } = useTranslation();
  const errorMessageRef = useRef(null);

  const description = useMemo(() => {
    if (typeof productLifetime === 'undefined') return '';

    return t(translationKeys[context].description, {
      lifetime: getLifetimeInfo(t, productLifetime),
    });
  }, [context, productLifetime, lang]);

  const durationString = useMemo(() => {
    if (typeof mediaDuration === 'undefined') return '';

    return getTranslatedMediaDuration(mediaDuration, t);
  }, [mediaDuration]);

  useEffect(() => {
    if (error && open) {
      errorMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [error]);

  return (
    <Transition
      as={Fragment}
      appear={true}
      show={open}
      enter="tw-transition-all tw-ease-out tw-duration-200"
      enterFrom="tw-opacity-0"
      enterTo="tw-opacity-100"
      leave="tw-transition-all tw-duration-300"
      leaveFrom="tw-opacity-100"
      leaveTo="tw-opacity-0"
    >
      <Dialog
        onClose={onCancel}
        className="tw-fixed tw-z-100 tw-inset-0 tw-overflow-y-auto"
      >
        <div className="medium-up:tw-flex tw-items-center tw-justify-center tw-min-h-screen">
          <Dialog.Overlay className="tw-fixed tw-inset-0 tw-bg-black tw-opacity-70 tw-z-[-1]" />

          <div className="tw-flex tw-flex-col tw-relative tw-bg-panel-gradient tw-rounded-lg small:tw-m-1 small:tw-px-8 small:tw-py-12 medium-up:tw-p-16 medium-up:tw-mx-auto medium-up:tw-max-w-4xl">
            <div className="tw-absolute tw-top-4 tw-right-4 medium-up:tw-top-8 medium-up:tw-right-8">
              <IconButton onClick={onCancel} transparent={true}>
                <Close size={40} />
              </IconButton>
            </div>

            <div className="tw-absolute tw-bottom-0 tw-left-[-11rem] tw-z-0 tw-w-9/12 small:tw-hidden">
              <Mediabox
                alt=""
                src="/static/images/img/ed-hat.png"
                aspectRatio="1_1"
                isBackgroundTransparent
              />
            </div>
            <PageTitle
              title={t(translationKeys[context].title)}
              size="large"
              classes="tw-relative tw-mb-12 medium-up:tw-w-8/12 medium-up:tw-ml-auto tw-z-1 small:tw-text-6xl"
            />

            <div className="tw-relative tw-z-1 medium-up:tw-w-8/12 tw-ml-auto ">
              <Typography classes="tw-mb-12">{description}</Typography>

              <ul className="tw-mb-16 tw-ml-12">
                <li className="tw-list-disc">
                  <strong>{t('title')}:</strong> {title}
                </li>
                <li className="tw-list-disc">
                  <strong>{t('lifetimeInfo')}:</strong>{' '}
                  {getLifetimeInfo(t, productLifetime)}
                </li>
                <li className="tw-list-disc">
                  <strong>{t('durationInfo')}:</strong> {durationString}
                </li>
                {mediaQuality && (
                  <li className="tw-list-disc">
                    <strong>{t('qualityInfo')}:</strong> {mediaQuality}
                  </li>
                )}
              </ul>
              <div className="tw-flex tw-flex-col">
                <Button
                  classes="tw-w-full tw-mb-4 tw-whitespace-normal"
                  title={`${t('checkoutModalOrderButton')} ${Number(
                    productPrice / 100
                  ).toLocaleString(lang, {
                    style: 'currency',
                    currency: 'EUR',
                  })}${'\u00B9'}`}
                  onClick={onConfirm}
                  startIcon={loading && <CircularProgress />}
                />
                {!!error && (
                  <div ref={errorMessageRef}>
                    <Typography
                      classes="tw-text-red-500 tw-mb-12"
                      dangerouslySetInnerHTML={{
                        __html: error,
                      }}
                    />
                  </div>
                )}
                <div className="medium-up:tw-flex tw-items-center">
                  <Button
                    color="secondary"
                    variant="outlined"
                    classes="tw-w-full medium-up:tw-w-1/2 tw-mb-4"
                    title={t('cancel')}
                    onClick={onCancel}
                  />
                  <TextLink
                    size="tiny"
                    classes="tw-w-full tw-text-center medium-up:tw-w-1/2 tw-mb-4 tw-pl-4"
                    text={t('informationTextAGB')}
                    linkDestination={t('informationUrlAGB')}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default DialogCheckout;
